import RecordList from '~/assets/javascript/modules/record/list';
import RecordListOptions from '~/assets/javascript/modules/record/list/options';
import { SearchEngine } from '~/assets/javascript/utils/search/engine';
import { LocalSearchStrategy } from '~/assets/javascript/utils/search/engine/strategies/local-search-strategy';
import { calculateFieldFilterOptions } from '~/assets/javascript/utils/table/field';

const facetKeys = ['group', 'filter'];
export const SEARCHABLE_KEY = '_objectStringified';
export const populateSearchableData = (records, { groupField, filterField, emptyLabel }) => records.map((record) => {
  const group = groupField ? calculateFieldFilterOptions(record[groupField.id], groupField, emptyLabel) : null;
  const filter = filterField ? calculateFieldFilterOptions(record[filterField.id], filterField, emptyLabel) : null;

  return {
    id: record.id,
    group: Array.isArray(group) ? group.flat() : group, // lookups can be nested arrays
    filter: Array.isArray(filter) ? filter.flat() : filter, // lookups can be nested arrays
    [SEARCHABLE_KEY]: JSON.stringify(record),
  };
});

export const state = () => ({
  additionalParams: {},
});

export const getters = {
  i18n:
    (_state, _getters) => useNuxtApp().$i18n,
  emptyLabel: (_state, { i18n }) => i18n.t('global.emptyLabel'),
  currentView:
    (_state, _getters, { view: viewState, studio: studioState, builderView: builderViewState }) => (
      studioState.inStudio ? builderViewState.view : viewState.view
    ),
  draftRecords:
    (_state, getters, { records: recordsState }) => getters
      .currentView
      ?.recordIds
      ?.map(id => recordsState.draftRecordsById[id])
      ?.filter(Boolean) || [],
  draftRecordsToView:
    (_state, _getters, { records: recordsState }) => view => view
      ?.recordIds
      ?.map(id => recordsState.draftRecordsById[id])
      ?.filter(Boolean) || [],
  searchStrategy: (_state, { emptyLabel, draftRecords, recordToolboxOptions, groupByFieldId, filterByFieldId }) => {
    const groupField = recordToolboxOptions.fieldIdMapping[groupByFieldId];
    const filterField = recordToolboxOptions.fieldIdMapping[filterByFieldId];
    const items = populateSearchableData(draftRecords, { groupField, filterField, emptyLabel });

    return new LocalSearchStrategy({
      items,
      searchableKeys: [SEARCHABLE_KEY],
      facetKeys,
      useFuzzySearch: false,
    });
  },
  searchEngine: (_state, { searchStrategy }) => new SearchEngine(searchStrategy),
  selectedFacets: (_state) => {
    const { $router } = useNuxtApp();

    return SearchEngine.extractSelectedFacetsFromQueryParams(
      $router.currentRoute.value.query,
      facetKeys,
    );
  },
  searchEngineResult: (_state, { searchEngine, selectedFacets }) => {
    const { $router } = useNuxtApp();
    const queryParams = $router.currentRoute.value.query;

    return searchEngine.search(queryParams.searchTerm || '', selectedFacets);
  },
  filteredRecordIds: (_state, { searchEngineResult }) => searchEngineResult.result?.map(result => result.id) || [],
  searchQuery: (_state, _getters) => {
    const { $router } = useNuxtApp();

    return $router.currentRoute.value.query.searchTerm;
  },
  hasSearch: (_state, getters) => Boolean(getters.searchQuery) || !_isEmpty(getters.selectedFacets),
  filteredRecords:
    (_state, getters) => getters.draftRecords.filter(record => getters.filteredRecordIds.includes(record.id)),
  recordToolbox:
    (_state, getters) => new RecordList({
      view: getters.currentView,
      records: getters.hasSearch ? getters.filteredRecords : getters.draftRecords,
      options: getters.recordToolboxOptions,
      emptyLabel: getters.emptyLabel,
    }),
  recordToolboxOptions:
    (state, getters) => new RecordListOptions(
      getters.currentView,
      getters.i18n?.locale,
      state.additionalParams,
    ),
  filterByFieldId: (_state, getters) => getters.recordToolboxOptions.filterByFieldId,
  groupByFieldId: (_state, getters) => getters.recordToolboxOptions.groupFieldId,
  records: (_state, getters) => getters.recordToolbox.records,
  sortedIds: (_state, getters) => getters.recordToolbox.sortedIds,
  findRecord: (_state, getters) => recordId => getters.recordToolbox.findRecord(recordId),
};

export const mutations = {
  setAdditionalParams(state, params) {
    state.additionalParams = params;
  },
};

export const actions = {
  updateAdditionalParams({ commit }, params) {
    commit('setAdditionalParams', params);
  },
};

export const namespaced = true;
